import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  InputAdornment,
  CircularProgress,
  Button,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Search as SearchIcon, Download as DownloadIcon } from '@mui/icons-material';
import Breadcrumbs from '../../components/Breadcrumbs';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const GlobalInventoryAudit = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ALL');
  const [organization, setOrganization] = useState('BOTH'); // BARE | EPL | BOTH
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [orderBy, setOrderBy] = useState('skuCreatedAt');
  const [orderDir, setOrderDir] = useState('desc'); // 'asc' | 'desc'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/inventory-skus?limit=1000`, { credentials: 'include' });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to load inventory SKUs');
      const mapped = (json.data || []).map((s) => ({
        id: s.id,
        sku: s.sku,
        batchNumber: s.inventory?.batchId || '-',
        skuCreatedAt: s.createdAt,
        batchCreatedAt: s.inventory?.createdAt || null,
        createdBy: s.createdByName || s.createdById || '-',
        packedBy: s.packedByName || s.packedById || '-',
        soldTo: s.soldToClinicId || '-',
        invoiceId: s.invoiceId || '-',
        soldAt: s.soldAt || null,
        deliveredAt: s.deliveredAt || null,
        costPerUnit: s.costPerUnit ?? null,
        salePrice: s.salePrice ?? null,
        rawProfit: s.rawProfit ?? (s.salePrice != null && s.costPerUnit != null ? Number(s.salePrice) - Number(s.costPerUnit) : null),
        status: s.status,
        organization: s.organization || s.inventory?.organization || '-'
      }));
      setRows(mapped);
      setError(null);
    } catch (e) {
      console.error(e);
      setError('Failed to load audit data');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const matches = !q || [r.sku, r.batchNumber, r.invoiceId, r.createdBy, r.packedBy, r.soldTo]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
      const statusOk = status === 'ALL' || r.status === status;
      const orgOk = organization === 'BOTH' || (r.organization || '').toUpperCase() === organization;
      const dateOk = (() => {
        if (!fromDate && !toDate) return true;
        const ts = r.skuCreatedAt ? new Date(r.skuCreatedAt).getTime() : 0;
        const fromOk = fromDate ? ts >= new Date(fromDate).getTime() : true;
        const toOk = toDate ? ts <= new Date(toDate).getTime() + 24*60*60*1000 - 1 : true;
        return fromOk && toOk;
      })();
      return matches && statusOk && orgOk && dateOk;
    });
  }, [rows, search, status, organization, fromDate, toDate]);

  const sorted = useMemo(() => {
    const data = [...filtered];
    data.sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      let cmp = 0;
      if (av == null && bv == null) cmp = 0; else if (av == null) cmp = -1; else if (bv == null) cmp = 1; else {
        if (orderBy.toLowerCase().includes('date') || orderBy.toLowerCase().includes('at')) {
          cmp = new Date(av).getTime() - new Date(bv).getTime();
        } else if (typeof av === 'number' && typeof bv === 'number') {
          cmp = av - bv;
        } else {
          cmp = String(av).localeCompare(String(bv));
        }
      }
      return orderDir === 'asc' ? cmp : -cmp;
    });
    return data;
  }, [filtered, orderBy, orderDir]);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const requestSort = (property) => {
    const isAsc = orderBy === property && orderDir === 'asc';
    setOrderDir(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : '-');
  const formatMoney = (n) => (n == null ? '-' : `$${Number(n).toFixed(2)}`);

  const exportCSV = () => {
    const headers = [
      'Organization','Batch Number','SKU','Batch Created','SKU Created','Created By','Packed By','Sold To','Invoice','Sold At','Delivered','Cost','Sale','Raw Profit','Status'
    ];
    const lines = filtered.map((r) => [
      r.organization,
      r.batchNumber,
      r.sku,
      formatDate(r.batchCreatedAt),
      formatDate(r.skuCreatedAt),
      r.createdBy,
      r.packedBy,
      r.soldTo,
      r.invoiceId,
      formatDate(r.soldAt),
      formatDate(r.deliveredAt),
      r.costPerUnit ?? '',
      r.salePrice ?? '',
      r.rawProfit ?? '',
      r.status
    ].map((v) => `"${String(v).replace(/"/g,'""')}"`).join(','));
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `global-inventory-audit-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Breadcrumbs items={[{ label: 'Admin', path: '/internal/admin' }, { label: 'Global Inventory Audit' }]} />

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Global Inventory Audit</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<DownloadIcon />} sx={{ textTransform: 'none' }} onClick={exportCSV}>Export CSV</Button>
        </Box>
      </Box>

      <Card sx={{ mb: 3, bgcolor: alpha('#5c725c', 0.02), border: '1px solid #e2e8f0' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search SKU, Batch, Invoice, User, Clinic"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Organization</InputLabel>
                <Select value={organization} label="Organization" onChange={(e) => setOrganization(e.target.value)}>
                  <MenuItem value="BOTH">Both</MenuItem>
                  <MenuItem value="BARE">BARE</MenuItem>
                  <MenuItem value="EPL">EPL</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="AVAILABLE">AVAILABLE</MenuItem>
                  <MenuItem value="ALLOCATED">ALLOCATED</MenuItem>
                  <MenuItem value="PICKED">PICKED</MenuItem>
                  <MenuItem value="PACKED">PACKED</MenuItem>
                  <MenuItem value="SHIPPED">SHIPPED</MenuItem>
                  <MenuItem value="DELIVERED">DELIVERED</MenuItem>
                  <MenuItem value="RETURNED">RETURNED</MenuItem>
                  <MenuItem value="DISPOSED">DISPOSED</MenuItem>
                  <MenuItem value="LOST">LOST</MenuItem>
                  <MenuItem value="EXPIRED">EXPIRED</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e)=>setFromDate(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e)=>setToDate(e.target.value)} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : error ? (
        <Card><CardContent><Typography color="error">{error}</Typography></CardContent></Card>
      ) : (
        <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={orderBy==='batchNumber'?orderDir:false}>
                  <TableSortLabel active={orderBy==='batchNumber'} direction={orderDir} onClick={()=>requestSort('batchNumber')}>Batch Number</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy==='sku'?orderDir:false}>
                  <TableSortLabel active={orderBy==='sku'} direction={orderDir} onClick={()=>requestSort('sku')}>SKU</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy==='batchCreatedAt'?orderDir:false}>
                  <TableSortLabel active={orderBy==='batchCreatedAt'} direction={orderDir} onClick={()=>requestSort('batchCreatedAt')}>Batch Created</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy==='skuCreatedAt'?orderDir:false}>
                  <TableSortLabel active={orderBy==='skuCreatedAt'} direction={orderDir} onClick={()=>requestSort('skuCreatedAt')}>SKU Created</TableSortLabel>
                </TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Packed By</TableCell>
                <TableCell>Sold To</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell sortDirection={orderBy==='soldAt'?orderDir:false}>
                  <TableSortLabel active={orderBy==='soldAt'} direction={orderDir} onClick={()=>requestSort('soldAt')}>Sold At</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy==='deliveredAt'?orderDir:false}>
                  <TableSortLabel active={orderBy==='deliveredAt'} direction={orderDir} onClick={()=>requestSort('deliveredAt')}>Delivered</TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy==='costPerUnit'?orderDir:false}>
                  <TableSortLabel active={orderBy==='costPerUnit'} direction={orderDir} onClick={()=>requestSort('costPerUnit')}>Cost</TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy==='salePrice'?orderDir:false}>
                  <TableSortLabel active={orderBy==='salePrice'} direction={orderDir} onClick={()=>requestSort('salePrice')}>Sale</TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy==='rawProfit'?orderDir:false}>
                  <TableSortLabel active={orderBy==='rawProfit'} direction={orderDir} onClick={()=>requestSort('rawProfit')}>Raw Profit</TableSortLabel>
                </TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{r.batchNumber}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.sku}</TableCell>
                  <TableCell>{formatDate(r.batchCreatedAt)}</TableCell>
                  <TableCell>{formatDate(r.skuCreatedAt)}</TableCell>
                  <TableCell>{r.createdBy}</TableCell>
                  <TableCell>{r.packedBy}</TableCell>
                  <TableCell>{r.soldTo}</TableCell>
                  <TableCell>{r.invoiceId}</TableCell>
                  <TableCell>{formatDate(r.soldAt)}</TableCell>
                  <TableCell>{formatDate(r.deliveredAt)}</TableCell>
                  <TableCell align="right">{formatMoney(r.costPerUnit)}</TableCell>
                  <TableCell align="right">{formatMoney(r.salePrice)}</TableCell>
                  <TableCell align="right">{formatMoney(r.rawProfit)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
          <Typography variant="body2" color="text.secondary">{filtered.length} records</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              select
              size="small"
              label="Rows"
              value={rowsPerPage}
              onChange={(e)=>{ setRowsPerPage(parseInt(e.target.value,10)); setPage(0); }}
            >
              {[10,25,50,100].map(n => (<MenuItem key={n} value={n}>{n}</MenuItem>))}
            </TextField>
            <Button disabled={page===0} onClick={()=>setPage((p)=>Math.max(0,p-1))}>Prev</Button>
            <Button disabled={(page+1)*rowsPerPage>=sorted.length} onClick={()=>setPage((p)=>p+1)}>Next</Button>
          </Box>
        </Box>
        </>
      )}
    </Box>
  );
};

export default GlobalInventoryAudit;


